/**
 * Operations Calendar Utilities
 *
 * Color mapping and helper functions for calendar events.
 * All dates are handled in America/Montreal timezone per CLAUDE.md requirements.
 */

import { parseISO, startOfMonth, startOfDay, addMonths } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import type { CalendarEventType, ProjectType } from './types';

/**
 * The canonical timezone for the application
 * Per CLAUDE.md: "IMPORTANT: Always use America / Montreal timezone"
 */
export const APP_TIMEZONE = 'America/Montreal';

// =============================================================================
// Color Mapping
// =============================================================================

// Note: Milestones were removed from calendar view - they now appear in the project activity log
export const EVENT_COLORS = {
  installation: { bg: '#3B82F6', border: '#2563EB', text: '#FFFFFF' },
  shipping: { bg: '#22C55E', border: '#16A34A', text: '#FFFFFF' },
  calibration: { bg: '#A855F7', border: '#9333EA', text: '#FFFFFF' },
  task: { bg: '#F97316', border: '#EA580C', text: '#FFFFFF' },
} as const;

export type EventColorKey = keyof typeof EVENT_COLORS;

export function getEventColor(sourceType: ProjectType | 'task') {
  return EVENT_COLORS[sourceType] || EVENT_COLORS.task;
}

// =============================================================================
// Event Type Labels
// =============================================================================

export const EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  project: 'Project',
  task: 'Task',
};

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  installation: 'Installation',
  shipping: 'Shipping',
  calibration: 'Calibration',
};

// =============================================================================
// Legend Items
// =============================================================================

export interface LegendItem {
  key: string;
  label: string;
  color: string;
  borderColor: string;
}

export const CALENDAR_LEGEND_ITEMS: LegendItem[] = [
  { key: 'installation', label: 'Installation', color: EVENT_COLORS.installation.bg, borderColor: EVENT_COLORS.installation.border },
  { key: 'shipping', label: 'Shipping', color: EVENT_COLORS.shipping.bg, borderColor: EVENT_COLORS.shipping.border },
  { key: 'calibration', label: 'Calibration', color: EVENT_COLORS.calibration.bg, borderColor: EVENT_COLORS.calibration.border },
  { key: 'task', label: 'Task', color: EVENT_COLORS.task.bg, borderColor: EVENT_COLORS.task.border },
];

// =============================================================================
// Date Helpers
// =============================================================================

/**
 * Format date for display in event details
 * Uses Montreal timezone for consistent display across all users
 */
export function formatEventDate(dateString: string): string {
  if (!dateString) {
    console.error('formatEventDate: Empty date string provided');
    return '-';
  }

  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) {
      console.error('formatEventDate: Invalid date string:', dateString);
      return '-';
    }
    return formatInTimeZone(date, APP_TIMEZONE, 'EEE, MMM d, yyyy');
  } catch (error) {
    console.error('formatEventDate: Error parsing date:', dateString, error);
    return '-';
  }
}

/**
 * Get month range for calendar view
 * Ensures dates are calculated in Montreal timezone
 */
export function getMonthRange(date: Date): { start: string; end: string } {
  // Convert the input date to Montreal timezone
  const zonedDate = toZonedTime(date, APP_TIMEZONE);

  // Get first day of current month in Montreal time
  const start = startOfMonth(zonedDate);

  // Get first day of next month (end boundary) in Montreal time
  const end = startOfMonth(addMonths(zonedDate, 1));

  return {
    start: formatInTimeZone(start, APP_TIMEZONE, 'yyyy-MM-dd'),
    end: formatInTimeZone(end, APP_TIMEZONE, 'yyyy-MM-dd'),
  };
}

/**
 * Convert FullCalendar date info to ISO string in Montreal timezone
 * FullCalendar provides dates in browser local time, but we need to interpret them as Montreal dates
 */
export function toISODateString(date: Date): string {
  if (!date || isNaN(date.getTime())) {
    console.error('toISODateString: Invalid date provided:', date);
    throw new Error('Invalid date');
  }

  // Convert to Montreal timezone and format as YYYY-MM-DD
  return formatInTimeZone(date, APP_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Get current date in Montreal timezone
 */
export function getNowInMontrealTz(): Date {
  return toZonedTime(new Date(), APP_TIMEZONE);
}

/**
 * Get start of current month in Montreal timezone
 */
export function getCurrentMonthStartInMontrealTz(): Date {
  const now = getNowInMontrealTz();
  return startOfMonth(now);
}

/**
 * Get start of day in Montreal timezone
 */
export function getStartOfDayInMontrealTz(date: Date): Date {
  const zonedDate = toZonedTime(date, APP_TIMEZONE);
  return startOfDay(zonedDate);
}

// =============================================================================
// Filter Helpers
// =============================================================================

/**
 * Convert filters to query string params
 */
export function filtersToParams(
  filters: {
    types: CalendarEventType[];
    projectTypes: ProjectType[];
    assigneeIds: string[];
    includeCompleted: boolean;
  },
  dateRange: { start: string; end: string }
): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {
    start: dateRange.start,
    end: dateRange.end,
  };

  // Only send types param if filtering (not all types selected)
  if (filters.types.length > 0 && filters.types.length < 2) {
    params.types = filters.types.join(',');
  }

  if (filters.projectTypes.length > 0 && filters.projectTypes.length < 3) {
    params.projectTypes = filters.projectTypes.join(',');
  }

  if (filters.assigneeIds.length > 0) {
    params.assigneeIds = filters.assigneeIds.join(',');
  }

  if (filters.includeCompleted) {
    params.includeCompleted = true;
  }

  return params;
}
